import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Type } from "../Type";

export type Props = {
  /** Title to display in the card header */
  title: string;
  /** Number of completed items */
  completedCount?: number;
  /** Total number of items */
  totalCount?: number;
  /** Content to render inside the card (action items) */
  children?: ReactNode;
  /** Content to render when loading */
  loadingContent?: ReactNode;
  /** Whether the card is in loading state */
  isLoading?: boolean;
  /** Message to display when there are no items */
  emptyMessage?: string;
  /** Additional className for the card container */
  className?: string;
};

/**
 * A styled card component for displaying action lists.
 * Used for both program phase actions and org admin actions.
 */
export function ActionListCard({
  title,
  completedCount,
  totalCount,
  children,
  loadingContent,
  isLoading = false,
  emptyMessage,
  className,
}: Props) {
  const hasItems = totalCount !== undefined && totalCount > 0;
  const showEmptyState = !isLoading && !hasItems && emptyMessage;
  const showCompletionCount =
    completedCount !== undefined && totalCount !== undefined && hasItems;

  const headerText = showCompletionCount
    ? `${title} (${completedCount} of ${totalCount} complete)`
    : isLoading
      ? "Loading actions..."
      : emptyMessage || title;

  return (
    <div
      className={twMerge(
        "flex flex-col gap-4 overflow-x-hidden",
        className
      )}
      data-testid="action-list-card"
    >
      <div className="flex flex-col gap-1">
        <Type variant={showEmptyState ? "h6" : "h5"}>{headerText}</Type>
      </div>
      {hasItems && children && (
        <div className="flex flex-col gap-2">{children}</div>
      )}
      {isLoading && loadingContent && (
        <div className="flex flex-col gap-2">{loadingContent}</div>
      )}
    </div>
  );
}

export default ActionListCard;
